import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
  Badge,
  Chip
} from '@mui/material';
import {
  Dashboard,
  Psychology,
  Analytics,
  AccountTree,
  TrendingUp,
  School
} from '@mui/icons-material';
import { InterviewSummary } from '../types';
import { ProgressiveLearning } from './ProgressiveLearning';
import { KnowledgeVisualization } from './KnowledgeVisualization';
import { AdvancedAnalytics } from './AdvancedAnalytics';

interface AdvancedDashboardProps {
  currentKnowledge?: InterviewSummary | null;
  recentQueries: string[];
  showAdvancedFeatures?: boolean;
  onToggleFeatures?: (show: boolean) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`advanced-tabpanel-${index}`}
      aria-labelledby={`advanced-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `advanced-tab-${index}`,
    'aria-controls': `advanced-tabpanel-${index}`,
  };
}

export function AdvancedDashboard({ 
  currentKnowledge, 
  recentQueries, 
  showAdvancedFeatures = true,
  onToggleFeatures 
}: AdvancedDashboardProps) {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleLevelUp = (newLevel: number) => {
    console.log(`Congratulations! You've reached level ${newLevel}!`);
    // Could show notification or animation here
  };

  const handleOptimization = (suggestion: any) => {
    console.log('Applying optimization:', suggestion);
    // Implement optimization logic here
  };

  if (!showAdvancedFeatures) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                🚀 Advanced AI Features Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unlock progressive learning, knowledge visualization, and advanced analytics
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={showAdvancedFeatures ? 'on' : 'off'}
              exclusive
              onChange={(e, value) => onToggleFeatures?.(value === 'on')}
            >
              <ToggleButton value="off">Basic</ToggleButton>
              <ToggleButton value="on">Advanced</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Dashboard sx={{ mr: 2, fontSize: '2rem' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Advanced AI Dashboard
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Progressive learning • Knowledge visualization • Advanced analytics
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={`${currentKnowledge?.tablesDiscovered.length || 0} Tables`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                icon={<AccountTree />}
              />
              <Chip 
                label={`${recentQueries.length} Queries`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                icon={<Psychology />}
              />
              <Chip 
                label={`${Math.round((currentKnowledge?.confidenceScore || 0) * 100)}% Confidence`}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                icon={<TrendingUp />}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="advanced dashboard tabs">
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <School sx={{ mr: 1 }} />
                  Progressive Learning
                  <Badge badgeContent="New" color="secondary" sx={{ ml: 1 }} />
                </Box>
              } 
              {...a11yProps(0)} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountTree sx={{ mr: 1 }} />
                  Knowledge Map
                </Box>
              } 
              {...a11yProps(1)} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Analytics sx={{ mr: 1 }} />
                  Advanced Analytics
                  <Badge badgeContent="AI" color="primary" sx={{ ml: 1 }} />
                </Box>
              } 
              {...a11yProps(2)} 
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <ProgressiveLearning
            currentKnowledge={currentKnowledge}
            recentQueries={recentQueries}
            onLevelUp={handleLevelUp}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <KnowledgeVisualization
            currentKnowledge={currentKnowledge}
            recentQueries={recentQueries}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <AdvancedAnalytics
            currentKnowledge={currentKnowledge}
            recentQueries={recentQueries}
            onOptimize={handleOptimization}
          />
        </TabPanel>
      </Card>
    </Box>
  );
}
